import { Button, FieldLegend, Tabs, TabsContent, TabsList, TabsTrigger } from '@glade-ai/ui';
import { useState } from 'react';

import getLoginsEnabled from '@/components/Auth/helpers/getLoginsEnabled';

import TermsAndPolicy from '../TermsAndPolicy';
import { type View } from '../types';

import SignInWithEmail from './SignInWithEmail';
import SignInWithPhone from './SignInWithPhone';

type AuthMethod = 'email' | 'phone';

type Props = {
  creatorSlug?: string;
  onChangeView: (view: View, data?: { email: string }) => void;
  initialAuthMethod?: AuthMethod;
  phoneNumber?: string;
  email?: string;
  isCreator?: boolean;
  hasFinishedOnboarding?: boolean;
  isCreatorView?: boolean;
};

const SignInForm: React.FC<Props> = ({
  creatorSlug,
  initialAuthMethod = 'email',
  onChangeView,
  phoneNumber,
  email,
  isCreator,
  hasFinishedOnboarding,
  isCreatorView,
}) => {
  const { isEmailLoginEnabled, isPhoneLoginEnabled } = getLoginsEnabled({ creatorSlug });
  const renderTabs = isEmailLoginEnabled && isPhoneLoginEnabled;
  const [authMethod, setAuthMethod] = useState<AuthMethod>(
    isPhoneLoginEnabled && !isEmailLoginEnabled
      ? 'phone'
      : !isPhoneLoginEnabled && isEmailLoginEnabled
        ? 'email'
        : isPhoneLoginEnabled && isEmailLoginEnabled
          ? initialAuthMethod
          : 'email',
  );

  const handleTapSignUp = (): void => onChangeView('signUp');

  // TODO(will): This outer `div` prevents a gap from separating the header and form here, otherwise it should be trimmed away.
  return (
    <div role='none'>
      <div role='none' className='mb-6'>
        <FieldLegend className='heading-sm'>Sign in</FieldLegend>
        {creatorSlug && (
          <p className='text-sm'>
            Need to create an account?{' '}
            <Button
              variant='link'
              className='!text-[--color-primary] px-0.5'
              onClick={handleTapSignUp}
              data-testid='auth.switchToSignUp'
            >
              Sign up.
            </Button>
          </p>
        )}
      </div>
      <Tabs value={authMethod} onValueChange={(nextValue: string) => setAuthMethod(nextValue as AuthMethod)}>
        {renderTabs && (
          <TabsList className='mb-4'>
            <TabsTrigger value='email'>With email address</TabsTrigger>
            <TabsTrigger value='phone'>With phone number</TabsTrigger>
          </TabsList>
        )}
        <TabsContent value='email'>
          <SignInWithEmail
            onChangeView={onChangeView}
            creatorSlug={creatorSlug}
            email={email}
            isCreator={isCreator}
            hasFinishedOnboarding={hasFinishedOnboarding}
          />
        </TabsContent>
        <TabsContent value='phone'>
          <SignInWithPhone
            onChangeView={onChangeView}
            creatorSlug={creatorSlug}
            phoneNumber={phoneNumber}
            isCreator={isCreator}
            hasFinishedOnboarding={hasFinishedOnboarding}
          />
        </TabsContent>
      </Tabs>
      <TermsAndPolicy isSignIn isCreatorView={isCreatorView} />
    </div>
  );
};

export default SignInForm;
